import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db, auth } from '../../firebase/config';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

// Importaciones del Contexto y Utilidades Centralizadas (Pilares del Canvas)
import { useSegurosData } from '../../context/SegurosDataContext';
import { cleanPayload } from '../../firebase/cleanPayload';

/**
 * ModalAnulacionContratos - Gestión de bajas y anulaciones de contratos ART.
 * Sincronizado reactivamente con el contexto global y protegido contra crashes y alertas invasivas.
 */
const ModalAnulacionContratos = ({ show, onClose, onSuccess, contrato, clientId }) => {
  // --- 1. CONSUMO DEL CONTEXTO MAESTRO GLOBAL ---
  const { dbMasters } = useSegurosData();

  // --- 2. ESTADOS LOCALES ---
  const [formData, setFormData] = useState({
    motivoAnulacion: '',
    fechaAnulacion: new Date().toISOString().split('T')[0],
    quienGestiono: '',
    observaciones: ''
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [localError, setLocalError] = useState(null);

  // --- 3. HIDRATACIÓN DINÁMICA DE LA BAJA ---
  useEffect(() => {
    if (show && contrato) {
      setFormData({
        motivoAnulacion: contrato.motivoAnulacion || '',
        fechaAnulacion: contrato.fechaAnulacion || new Date().toISOString().split('T')[0],
        quienGestiono: contrato.quienGestionoAnulacion || '',
        observaciones: contrato.observacionesAnulacion || ''
      });
      setLocalError(null);
    }
  }, [show, contrato]);

  // --- 4. MEMORIZACIÓN Y ORDENAMIENTO EN MEMORIA LOCAL (useMemo) ---
  const sortedColaboradores = useMemo(() => {
    return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
        (a.nombre || '').localeCompare(b.nombre || '')
    );
  }, [dbMasters?.colaboradores]);

  // --- 5. HANDLERS DE ACCIÓN ---
  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);
    setLocalError(null);

    try {
      const client_id_safe = clientId || 'client_temp_123';
      const contratoRef = doc(db, `public/data/clientes/${client_id_safe}/contratos`, contrato.id);
      
      // Armamos payload de actualización de contrato raíz
      const updateData = {
        estadoPoliza: 'Anulada',
        motivoAnulacion: formData.motivoAnulacion,
        fechaAnulacion: formData.fechaAnulacion,
        quienGestionoAnulacion: formData.quienGestiono,
        observacionesAnulacion: formData.observaciones,
        
        // --- AUDITORÍA DE BAJA REQUERIDA ---
        ultimaModificacion: serverTimestamp()
      };

      // Aplicamos la limpieza profunda de campos vacíos para no guardar basura
      const cleanedUpdateData = cleanPayload(updateData, true, false);

      await updateDoc(contratoRef, cleanedUpdateData);
      
      if (onSuccess) {
          onSuccess('Contrato anulado correctamente.', { ...contrato, ...cleanedUpdateData });
      }
      onClose();
    } catch (error) {
      console.error("Error crítico procesando la anulación del contrato:", error);
      setLocalError('Error al procesar la baja del contrato: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1300] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 animate-fade-in flex flex-col max-h-[90vh] transition-all duration-300">
        
        {/* CABECERA DEL MODAL (Estilo Rojo Alerta) */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-red-100 bg-red-50 flex-shrink-0 text-left select-none">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-full shadow-sm text-red-500 ring-1 ring-red-100">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                </div>
                <div>
                    <h3 className="text-lg font-black text-slate-800 leading-none">Anular Contrato ART</h3>
                    <p className="text-[10px] text-red-600 font-black uppercase mt-1.5 tracking-wider">Esta acción es irreversible</p>
                </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white/80 rounded-full">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>

        {/* CUERPO DEL FORMULARIO */}
        <form id="anularContratoForm" onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-grow bg-white scrollbar-thin scrollbar-track-transparent text-left">
            
            {/* Banner de error dinámico (Reemplazo de alert) */}
            {localError && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs font-bold flex items-center gap-2 animate-fade-in pl-1">
                     <svg className="w-5 h-5 text-red-600 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                     <span>{localError}</span>
                </div>
            )}

            {/* MOTIVO ANULACIÓN */}
            <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Motivo de la Anulación *</label>
                <div className="relative">
                    <select 
                        name="motivoAnulacion" 
                        value={formData.motivoAnulacion} 
                        onChange={handleChange} 
                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none bg-white h-[46px] focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all appearance-none cursor-pointer pr-10" 
                        required
                    >
                        <option value="">Seleccione un motivo...</option>
                        <option value="BAJA DE EMPLEADOS">BAJA DE EMPLEADOS</option>
                        <option value="CAMBIO DE ART POR AUMENTO">CAMBIO DE ART POR AUMENTO</option>
                        <option value="CAMBIO DE ART POR DISCONFORMIDAD">CAMBIO DE ART POR DISCONFORMIDAD</option>
                        <option value="FALTA DE PAGO">FALTA DE PAGO</option>
                        <option value="CIERRE DE EMPRESA">CIERRE DE EMPRESA</option>
                        <option value="DESISTIMIENTO">DESISTIMIENTO</option>
                        <option value="OTRO">OTRO</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-slate-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"></path></svg>
                    </div>
                </div>
            </div>

            {/* FECHA Y QUIÉN GESTIONÓ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Fecha de Baja *</label>
                    <input type="date" name="fechaAnulacion" value={formData.fechaAnulacion} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white h-[46px] focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all" required />
                </div>
                <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">¿Quién Gestionó? *</label>
                    <div className="relative">
                        <select 
                            name="quienGestiono" 
                            value={formData.quienGestiono} 
                            onChange={handleChange} 
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white h-[46px] focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all appearance-none cursor-pointer pr-10" 
                            required
                        >
                            <option value="">Seleccione...</option>
                            {sortedColaboradores.map(c => (
                                <option key={c.id} value={c.nombre}>{c.nombre}</option>
                            ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-slate-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* OBSERVACIONES */}
            <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 pl-1">Observaciones</label>
                <textarea 
                    name="observaciones" 
                    value={formData.observaciones} 
                    onChange={handleChange} 
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 outline-none bg-white min-h-[100px] resize-none focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all placeholder:text-slate-300" 
                    placeholder="Escriba aquí los detalles..."
                />
            </div>

        </form>

        {/* BOTONERA DE ACCIÓN */}
        <div className="px-6 py-4 bg-gray-50 border-t border-slate-100 flex justify-end gap-3 flex-shrink-0 select-none">
            <button onClick={onClose} className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold hover:bg-slate-100 shadow-sm text-xs uppercase tracking-wider transition-all transform active:scale-95">
                Cancelar
            </button>
            <button 
                type="submit" 
                form="anularContratoForm"
                disabled={isSaving}
                className="px-8 py-2.5 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition shadow-lg shadow-red-100 disabled:opacity-50 text-xs uppercase tracking-wider transform active:scale-95 flex items-center min-w-[180px] justify-center animate-fade-in"
            >
                {isSaving ? 'Procesando...' : 'Confirmar Anulación'}
            </button>
        </div>
        
      </div>
    </div>,
    document.body
  );
};

export default ModalAnulacionContratos;